import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaPhone,
  FaPhoneSlash,
  FaTimesCircle,
  FaUser,
  FaUsers,
  FaStar,
  FaCalendarCheck,
  FaUserCheck,
  FaUserPlus,
  FaEllipsisV,
  FaSpinner,
  FaDownload,
  FaSyncAlt,
} from "react-icons/fa";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import html2canvas from "html2canvas-pro";
import { jsPDF } from "jspdf";
import SummaryApi from "../common";
import { useNavigate } from "react-router-dom";
const { RangePicker } = DatePicker;

/* ------------------------------------------------------------------ */
/* THEME                                                              */
/* ------------------------------------------------------------------ */
const THEME = {
  primary: "rgb(23, 44, 87)",
  primaryLight: "rgba(23, 44, 87, 0.08)",
  primaryHover: "rgba(23, 44, 87, 0.9)",
};

const AUTH_TOKEN = "Bearer sandeep_university_916";

/* ------------------------------------------------------------------ */
/* FILTERS + DATE RANGE                                               */
/* ------------------------------------------------------------------ */
const FILTERS = [
  { key: "today", label: "Today", days: 0 },
  { key: "7days", label: "7 days", days: 7 },
  { key: "30days", label: "30 days", days: 30 },
  { key: "all", label: "All", days: null },
];

const API_FMT = "YYYY-MM-DD";
const UI_FMT = "DD-MM-YYYY";

const RANGE_PRESETS = [
  { label: "Last 7 Days", value: () => [dayjs().subtract(6, "day"), dayjs()] },
  { label: "Last 14 Days", value: () => [dayjs().subtract(13, "day"), dayjs()] },
  { label: "Last 30 Days", value: () => [dayjs().subtract(29, "day"), dayjs()] },
  { label: "This Month", value: () => [dayjs().startOf("month"), dayjs()] },
];

const getTodayRange = () => [dayjs(), dayjs()];

const getDateRange = (key, customRange) => {
  if (key === "all") {
    return {
      start_date: customRange[0].format(API_FMT),
      end_date: customRange[1].format(API_FMT),
    };
  }
  const f = FILTERS.find((x) => x.key === key);
  const end = dayjs();
  return {
    start_date: end.subtract(f.days, "day").format(API_FMT),
    end_date: end.format(API_FMT),
  };
};

const toDisplay = (s) => dayjs(s).format(UI_FMT);

const getRangeText = (start_date, end_date) =>
  start_date === end_date
    ? toDisplay(end_date)
    : `${toDisplay(start_date)} to ${toDisplay(end_date)}`;

const disableFuture = (current) => current && current.isAfter(dayjs(), "day");

/* ------------------------------------------------------------------ */
/* API LAYER                                                          */
/* ------------------------------------------------------------------ */

const TEAM_LEADS_URL =
  SummaryApi?.getAllTL?.url ||
  "http://192.168.1.190/multi_university_apis/employee/team_leads.php";

const headers = {
  "Content-Type": "application/json",
  Authorization: AUTH_TOKEN,
};

// Fetch list of team leads
const fetchTeamLeads = async (filterKey) => {
  const res = await fetch(TEAM_LEADS_URL, { method: "GET", headers });
  if (!res.ok) throw new Error("Failed to fetch team leads");
  const json = await res.json();
  if (!json?.status || !Array.isArray(json.data)) {
    throw new Error("Invalid team leads response");
  }
  return json.data.map((tl) => ({
    id: String(tl.employee_id),
    name: tl.full_name,
    memberType: tl.member_type,
    mobile: tl.mobile_number,
    assignedCount: Number(tl.assigned_employee_count || 0),
  }));
};

// Common aggregator across rows and summary fallbacks
const extractReportTotals = (json) => {
  const rows = Array.isArray(json?.data) ? json.data : [];
  const summary = json?.summary || {};

  const agg = rows.reduce(
    (acc, r) => ({
      total: acc.total + Number(r.total_calls_count || 0),
      interested: acc.interested + Number(r.total_interested_calls_count || 0),
      mostInterested:
        acc.mostInterested + Number(r.total_most_interested_calls_count || 0),
      notInterested:
        acc.notInterested + Number(r.total_not_interested_calls_count || 0),
      notAnswered:
        acc.notAnswered + Number(r.total_not_answered_calls_count || 0),
      callBack:
        acc.callBack +
        Number(r.total_call_back_count || r.total_call_back_calls_count || 0),
    }),
    {
      total: 0,
      interested: 0,
      mostInterested: 0,
      notInterested: 0,
      notAnswered: 0,
      callBack: 0,
    }
  );

  // If callback was not present in the individual rows, read from summary
  if (agg.callBack === 0 && summary.total_call_back_count != null) {
    agg.callBack = Number(summary.total_call_back_count || 0);
  }

  // Fallback for total count if rows are empty but summary exists
  if (agg.total === 0 && summary.total_students != null) {
    agg.total = Number(summary.total_students || 0);
  }

  return agg;
};

// Fetch per-team-lead aggregated report for given date range
const fetchTeamLeadReport = async (teamLeadId, startDate, endDate) => {
  const res = await fetch(SummaryApi.getEmployeesWeeklyReportByTeamLead.url, {
    method: SummaryApi.getEmployeesWeeklyReportByTeamLead.method,
    headers,
    body: JSON.stringify({
      team_lead_emp_id: teamLeadId,
      from_date: startDate,
      to_date: endDate,
    }),
  });
  if (!res.ok) throw new Error(`Failed to fetch report for TL ${teamLeadId}`);
  const json = await res.json();
  return extractReportTotals(json);
};

const fetchTeamLeadReportAll = async (teamLeadId) => {
  const res = await fetch(SummaryApi.getAllEmployeeWeeklyReportByTeamLead.url, {
    method: SummaryApi.getAllEmployeeWeeklyReportByTeamLead.method,
    headers,
    body: JSON.stringify({
      team_lead_emp_id: teamLeadId,
    }),
  });
  if (!res.ok) throw new Error(`Failed to fetch report for TL ${teamLeadId}`);
  const json = await res.json();
  return extractReportTotals(json);
};

// Build the row shape EmployeeCard expects
const buildEmployeeRow = (tl, agg) => {
  const total = agg.total;
  const notAnswered = agg.notAnswered;
  const contacted = Math.max(total - notAnswered, 0);
  const callBack = agg.callBack || 0;

  return {
    id: tl.id,
    name: tl.name,
    assignedCount: tl.assignedCount,
    total,
    contacted,
    notAnswered,
    followups: 0,
    enrolled: agg.interested,
    mostInterested: agg.mostInterested,
    interested: agg.interested,
    callBack,
    others: Math.max(
      total -
      (agg.mostInterested +
        agg.interested +
        callBack +
        agg.notInterested +
        notAnswered),
      0
    ),
    notInterested: agg.notInterested,
  };
};

export const fetchPerformance = async ({ start_date, end_date, filterKey }) => {
  const teamLeads = await fetchTeamLeads(filterKey);

  const results = await Promise.all(
    teamLeads.map(async (tl) => {
      try {
        const agg =
          filterKey === "all"
            ? await fetchTeamLeadReportAll(tl.id)
            : await fetchTeamLeadReport(tl.id, start_date, end_date);

        return buildEmployeeRow(tl, agg);
      } catch (err) {
        console.error(`Failed to fetch data for team lead ${tl.name}:`, err);
        return buildEmployeeRow(tl, {
          total: 0,
          interested: 0,
          mostInterested: 0,
          notInterested: 0,
          notAnswered: 0,
          callBack: 0,
        });
      }
    })
  );

  return { employees: results };
};

/* ------------------------------------------------------------------ */
/* HELPERS                                                            */
/* ------------------------------------------------------------------ */
const n = (v) => Number(v || 0).toLocaleString("en-IN");
const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);

/* ------------------------------------------------------------------ */
/* SMALL UI PIECES                                                    */
/* ------------------------------------------------------------------ */
const Card = ({ className = "", children }) => (
  <div
    className={`bg-white/90 rounded-2xl shadow-[0_4px_20px_rgba(30,64,175,0.08)] border border-white ${className}`}
  >
    {children}
  </div>
);

const IconBadge = ({ className, children }) => (
  <div
    className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white text-lg shadow-md ${className}`}
  >
    {children}
  </div>
);

const CountCell = ({ icon, label, value, sub, color, bg }) => (
  <div className={`px-3 py-3 text-center ${bg}`}>
    <div className={`flex items-center justify-center gap-1.5 text-[11px] font-semibold ${color}`}>
      {icon}
      <span className="text-slate-600">{label}</span>
    </div>
    <div className="text-lg font-bold text-slate-800 mt-1 leading-tight">{value}</div>
    <div className="text-[11px] text-slate-500 h-4">{sub}</div>
  </div>
);

const SectionTitle = ({ children }) => (
  <div className="flex items-center gap-3 mb-3 mt-1">
    <span className="text-[11px] font-bold tracking-widest text-slate-400">{children}</span>
    <div className="flex-1 h-px bg-slate-200" />
  </div>
);

const Bar = ({ icon, label, percent, value, total, gradient, iconBg }) => (
  <div className="flex items-center gap-3">
    <div className="w-9 shrink-0">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${iconBg}`}>
        {icon}
      </div>
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[11px] font-bold tracking-wide text-slate-800 mb-1">{label}</div>
      <div className="h-[18px] rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full rounded-full flex items-center justify-center text-[10px] font-semibold text-white transition-all duration-500 ${gradient}`}
          style={{ width: `${Math.max(percent, 8)}%` }}
        >
          {percent}%
        </div>
      </div>
    </div>
    <div className="w-32 text-right text-sm shrink-0">
      <span className="font-bold text-slate-800">{n(value)}</span>
      <span className="text-slate-400"> / {n(total)}</span>
    </div>
  </div>
);

const BLUE = "bg-gradient-to-r from-blue-600 to-blue-400";
const RED = "bg-gradient-to-r from-red-600 to-red-400";
const GREEN = "bg-gradient-to-r from-emerald-600 to-emerald-400";
const AMBER = "bg-gradient-to-r from-amber-500 to-amber-300";

const EmployeeCard = ({ e }) => {
  const total = e.total;
  const notAnswered = e.notAnswered ?? total - e.contacted;
  const navigate = useNavigate();

  const handleRowClick = () => {
    const encodedId = btoa(String(e.id));
    sessionStorage.setItem("H7Z4L9VD2XK5JW8QRYT6", encodedId);
    navigate("/my-employees");
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-4 min-w-0">
          <IconBadge className="bg-gradient-to-br from-blue-500 to-blue-600 !rounded-full !w-12 !h-12 shrink-0">
            <FaUser />
          </IconBadge>

          <div className="min-w-0">
            <h3 className="text-xl font-bold text-slate-800">{e.name}</h3>

            <div className="flex items-center gap-2 mt-2">
              <button
                type="button"
                onClick={handleRowClick}
                className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1.5 text-blue-800 hover:bg-blue-100 transition"
              >
                <FaUser size={12} />
                <span className="text-sm font-semibold">{e.name}</span>
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-bold">
                  {e.assignedCount} Employees
                </span>
              </button>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={handleRowClick}
          title={`View employees of ${e.name}`}
          aria-label={`View employees of ${e.name}`}
          className="shrink-0 rounded-lg p-2 text-slate-500 hover:bg-indigo-50 hover:text-indigo-600 transition"
        >
          <FaEllipsisV />
        </button>
      </div>

      <div className="rounded-2xl border border-blue-100 bg-blue-100 mb-6 overflow-hidden">
        <div className="grid grid-cols-2 sm:grid-cols-3 2xl:grid-cols-6 gap-px">
          <CountCell
            icon={<FaUsers size={11} />}
            color="text-indigo-500"
            bg="bg-indigo-50"
            label="Total Assigned"
            value={n(total)}
            sub="Leads"
          />
          <CountCell
            icon={<FaPhone size={11} />}
            color="text-blue-500"
            bg="bg-blue-50"
            label="Contacted"
            value={n(e.contacted)}
            sub={`${pct(e.contacted, total)}%`}
          />
          <CountCell
            icon={<FaTimesCircle size={11} />}
            color="text-red-500"
            bg="bg-red-50"
            label="Not Answered"
            value={n(notAnswered)}
            sub={`${pct(notAnswered, total)}%`}
          />
          <CountCell
            icon={<FaStar size={11} />}
            color="text-amber-500"
            bg="bg-amber-50"
            label="Most Interested"
            value={n(e.mostInterested)}
            sub={`${pct(e.mostInterested, total)}%`}
          />
          <CountCell
            icon={<FaUserPlus size={11} />}
            color="text-emerald-600"
            bg="bg-emerald-50"
            label="Total Interested"
            value={n(e.interested)}
            sub={`${pct(e.interested, total)}%`}
          />
          <CountCell
            icon={<FaCalendarCheck size={11} />}
            color="text-violet-500"
            bg="bg-violet-50"
            label="Call Back"
            value={n(e.callBack)}
            sub={`${pct(e.callBack, total)}%`}
          />
        </div>
      </div>

      <SectionTitle>LEAD STATUS</SectionTitle>
      <div className="space-y-4 mb-6">
        <Bar
          icon={<FaStar size={12} />}
          iconBg="bg-amber-50 text-amber-500"
          label="MOST INTERESTED"
          percent={pct(e.mostInterested, total)}
          value={e.mostInterested}
          total={total}
          gradient={AMBER}
        />
        <Bar
          icon={<FaUserCheck size={12} />}
          iconBg="bg-emerald-50 text-emerald-600"
          label="INTERESTED"
          percent={pct(e.interested, total)}
          value={e.interested}
          total={total}
          gradient={GREEN}
        />
        <Bar
          icon={<FaCalendarCheck size={12} />}
          iconBg="bg-violet-50 text-violet-600"
          label="CALL BACK"
          percent={pct(e.callBack, total)}
          value={e.callBack}
          total={total}
          gradient={BLUE}
        />
        <Bar
          icon={<FaUsers size={12} />}
          iconBg="bg-blue-50 text-blue-600"
          label="OTHERS"
          percent={pct(e.others, total)}
          value={e.others}
          total={total}
          gradient={BLUE}
        />
        <Bar
          icon={<FaTimesCircle size={12} />}
          iconBg="bg-red-50 text-red-500"
          label="NOT INTERESTED"
          percent={pct(e.notInterested, total)}
          value={e.notInterested}
          total={total}
          gradient={RED}
        />
      </div>

      <SectionTitle>CALL STATUS</SectionTitle>
      <div className="space-y-4">
        <Bar
          icon={<FaPhone size={12} />}
          iconBg="bg-blue-50 text-blue-600"
          label="CONTACTED"
          percent={pct(e.contacted, total)}
          value={e.contacted}
          total={total}
          gradient={BLUE}
        />
        <Bar
          icon={<FaPhoneSlash size={12} />}
          iconBg="bg-red-50 text-red-500"
          label="NOT ANSWERED"
          percent={pct(notAnswered, total)}
          value={notAnswered}
          total={total}
          gradient={RED}
        />
      </div>
    </Card>
  );
};

/* ------------------------------------------------------------------ */
/* MAIN COMPONENT                                                     */
/* ------------------------------------------------------------------ */
const PerformanceOverview = () => {
  const [filterKey, setFilterKey] = useState("today");
  const [customRange, setCustomRange] = useState(getTodayRange);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const reportRef = useRef(null);
  const activeFetchIdRef = useRef(0);

  const { start_date: startDate, end_date: endDate } = useMemo(
    () => getDateRange(filterKey, customRange),
    [filterKey, customRange]
  );

  const rangeText = useMemo(
    () => getRangeText(startDate, endDate),
    [startDate, endDate]
  );

  // Reusable load data function (returns a promise)
  const loadData = useCallback(() => {
    const fetchId = ++activeFetchIdRef.current;
    setLoading(true);

    return fetchPerformance({
      start_date: startDate,
      end_date: endDate,
      filterKey,
    })
      .then((d) => {
        if (fetchId === activeFetchIdRef.current) {
          setData(d);
        }
        return d;
      })
      .catch((err) => {
        console.error("Performance fetch failed:", err);
      })
      .finally(() => {
        if (fetchId === activeFetchIdRef.current) {
          setLoading(false);
        }
      });
  }, [startDate, endDate, filterKey]);

  useEffect(() => {
    loadData();
    return () => {
      activeFetchIdRef.current++;
    };
  }, [loadData]);

  const label = useMemo(
    () => FILTERS.find((f) => f.key === filterKey).label.toUpperCase(),
    [filterKey]
  );

  const handleReset = () => {
    setFilterKey("today");
    setCustomRange(getTodayRange());
  };

  const handleRangeChange = (values) => {
    setCustomRange(
      values && values[0] && values[1] ? [values[0], values[1]] : getTodayRange()
    );
  };

  const handleDownload = async () => {
    if (!reportRef.current || loading || downloading) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#FFFFFF",
        onclone: (clonedDoc) => {
          const root = clonedDoc.querySelector("[data-pdf-root]");
          if (!root) return;
          root.style.padding = "24px";
          root.style.background = "#FFFFFF";

          const header = clonedDoc.createElement("div");
          header.style.cssText =
            "display:flex;align-items:center;justify-content:space-between;gap:16px;" +
            "margin-bottom:20px;padding-bottom:16px;border-bottom:1px solid #E2E8F0;";
          header.innerHTML = `
            <div>
              <div style="font-size:20px;font-weight:700;letter-spacing:0.025em;color:#172554;">
                TEAM LEAD PERFORMANCE REPORT
              </div>
            </div>
            <div style="display:flex;align-items:center;gap:8px;padding:8px 16px;border-radius:9999px;
                        background:#eff6ff;border:1px solid #DBEAFE;font-size:14px;font-weight:600;color:#1e3a8a;">
              <span>${startDate === endDate ? "Report Date" : "Report Period"} : ${rangeText}</span>
            </div>`;
          root.insertBefore(header, root.firstChild);

          const footer = clonedDoc.createElement("div");
          footer.style.cssText =
            "margin-top:20px;padding-top:12px;border-top:1px solid #E2E8F0;" +
            "text-align:right;font-size:12px;color:#64748b;";
          footer.textContent = `Generated on ${dayjs().format("DD-MM-YYYY hh:mm A")}`;
          root.appendChild(footer);
        },
      });

      const img = canvas.toDataURL("image/png");
      const w = canvas.width / 2;
      const h = canvas.height / 2;

      const pdf = new jsPDF({
        orientation: w > h ? "landscape" : "portrait",
        unit: "px",
        format: [w, h],
        hotfixes: ["px_scaling"],
      });
      pdf.addImage(img, "PNG", 0, 0, w, h);

      const fileSuffix =
        startDate === endDate ? startDate : `${startDate}_to_${endDate}`;
      pdf.save(`Team-Lead-Performance-Report-${fileSuffix}.pdf`);
    } catch (err) {
      console.error("PDF download failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-4 mb-5">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <h2
            className="text-base md:text-lg font-bold tracking-wide shrink-0"
            style={{ color: THEME.primary }}
          >
            {filterKey === "all"
              ? "TEAM LEAD PERFORMANCE REPORT"
              : `TEAM LEAD PERFORMANCE REPORT — ${label} (${rangeText})`}
          </h2>

          <div className="flex flex-col gap-3 lg:items-end">
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="inline-flex bg-slate-50 rounded-full border border-slate-200 p-1 shadow-sm">
                {FILTERS.map((f) => {
                  const isActive = filterKey === f.key;
                  return (
                    <button
                      key={f.key}
                      onClick={() => setFilterKey(f.key)}
                      style={isActive ? { backgroundColor: THEME.primary } : {}}
                      className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-all ${isActive ? "text-white shadow-sm" : "text-slate-600 hover:bg-white"
                        }`}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>

              {/* Refresh Button */}
              <button
                type="button"
                onClick={() => loadData()}
                disabled={loading}
                title="Refresh report data"
                aria-label="Refresh report data"
                style={{ color: THEME.primary }}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed text-xs font-semibold transition-all"
              >
                <FaSyncAlt
                  size={12}
                  className={loading ? "animate-spin" : "transition-transform"}
                />
                <span>Refresh</span>
              </button>

              {filterKey === "all" && (
                <button
                  type="button"
                  onClick={handleReset}
                  title="Reset to default"
                  aria-label="Reset to default"
                  style={{ color: THEME.primary }}
                  className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-white border border-slate-200 shadow-sm hover:rotate-180 transition-all duration-300"
                >
                  <FaSyncAlt size={13} />
                </button>
              )}

              <button
                onClick={handleDownload}
                disabled={loading || downloading || !data}
                style={{ backgroundColor: THEME.primary }}
                onMouseEnter={(e) => {
                  if (!loading && !downloading && data) {
                    e.currentTarget.style.backgroundColor = THEME.primaryHover;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading && !downloading && data) {
                    e.currentTarget.style.backgroundColor = THEME.primary;
                  }
                }}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold text-white shadow-sm hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              >
                {downloading ? <FaSpinner className="animate-spin" /> : <FaDownload />}
                {downloading ? "Generating..." : "Download"}
              </button>
            </div>

            {filterKey === "all" && (
              <RangePicker
                value={customRange}
                onChange={handleRangeChange}
                presets={RANGE_PRESETS}
                format={UI_FMT}
                allowClear={false}
                disabledDate={disableFuture}
                placeholder={["Start date", "End date"]}
              />
            )}
          </div>
        </div>
      </div>

      {!data && loading ? (
        <div className="flex justify-center py-24">
          <FaSpinner className="animate-spin text-3xl" style={{ color: THEME.primary }} />
        </div>
      ) : (
        <div ref={reportRef} data-pdf-root>
          <div
            className={`grid grid-cols-1 xl:grid-cols-2 gap-5 transition-opacity duration-200 ${loading ? "opacity-50" : "opacity-100"
              }`}
          >
            {data?.employees?.map((e) => (
              <EmployeeCard key={e.id} e={e} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceOverview;